<template>
  <div class="flex flex-col w-full">
    <div class="tabs mb-5">
      <button
        v-for="tab in tabs"
        :key="tab"
        class="tab"
        :class="{ active: activeTab === tab }"
        @click="setActiveTab(tab)"
      >
        {{ tab }}
      </button>
    </div>
    <div class="sub-tabs">
      <button
        v-for="subTab in subTabs"
        :key="subTab.key"
        class="sub-tab"
        :class="{ active: activeSubTab === subTab.key }"
        :disabled="subTab.disabled"
        @click="setActiveSubTab(subTab.key)"
      >
        {{ subTab.label }}
      </button>
    </div>
    <div class="chart-container">
      <div class="w-full h-full p-5">
        <BarChart
          :data="chartData"
          :options="chartOptions"
          :plugins="chartPlugins"
          :height="600"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {includes} from "lodash";
import { mapGetters } from 'vuex';

import BarChart from '@/components/barChart';
import {handleValidateAndFormatNumber} from "@/utils/inputFormatter";

export default {
  name: 'MajorAssumption',
  components: {
    BarChart
  },
  props: {
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  watch: {
    type: {
      handler() {
        this.fetchMajorAssumptions()
      }
    },

    filters: {
      deep: true,
      handler() {
        this.fetchMajorAssumptions()
      }
    }
  },
  data() {
    return {
      tabTypeMap: {
        'Market & Financial': 'MARKET_AND_FINANCIAL',
        'Operational Efficiency': 'OPERATIONAL_EFFICIENCY',
        'Volume & Sales': 'VOLUME_AND_SALES',
        'Chartering & Deployment': 'CHARTERING_AND_DEPLOYMENT',
        'Commercial Adjustment': 'COMMERCIAL_ADJUSTMENT'
      },
      tabs: [
        'Market & Financial',
        'Operational Efficiency',
        'Volume & Sales', 
        'Chartering & Deployment',
        'Commercial Adjustment'
      ],
      type: 'MARKET_AND_FINANCIAL',
      subTabsMap: {
        'Market & Financial': [
          { key: 'exchange', label: 'Exchange Rate (IDR/USD)' },
          { key: 'fuel', label: 'Fuel Price (USD/L)' }
        ],
        'Operational Efficiency': [
          { key: 'fuel_consumption', label: 'Fuel Consumption (Liter)' },
          { key: 'fleet_utilization', label: 'Fleet Utilization (%)' },
          { key: 'barge_capacity', label: 'Barge Capacity (Ton)' },
          { key: 'demmurage_per_dispatch', label: 'Demurrage / Dispact (USD/Ton)' },
          { key: 'fts_at_samarinda', label: 'FTS at Samarinda (USD/Ton)' }
        ],
        'Volume & Sales': [
          { key: 'volume_exc_3rd_party', label: 'Sale Volume (Ton) Exc. 3rd Party and Direct Barge' },
          { key: 'volume', label: 'Sale Volume (Ton)' }
        ],
        'Chartering & Deployment': [
          { key: 'total_spot_charter', label: 'Total Spot Charter (Trip)' },
          { key: 'trip_own_coal', label: 'Own Coal (Trip)' },
          { key: 'trip_third_party_coal', label: '3rd Party Coal (Trip)' },
          { key: 'time_charter_rate', label: 'Time Charter Rate (USD/Month) (Exc. VAT)' }
        ],
        'Commercial Adjustment': [
          { key: 'bonus_and_penalty', label: 'Bonus / Penalty (USD/Ton)', disabled: false }
        ]
      },
      activeTab: 'Market & Financial',
      activeSubTab: 'exchange'
    };
  },
  mounted() {
    this.fetchMajorAssumptions()
  },
  computed: {
    ...mapGetters({
      majorAssumptions: 'CostAnalysis/majorAssumptions'
    }),
    subTabs() {
      return this.subTabsMap[this.activeTab] || [];
    },
    yAxisLabel() {
      const label = (this.subTabs.find(st => st.key === this.activeSubTab)?.label || '');
      const match = label.match(/\(([^)]+)\)/);
      return match ? match[1] : label;
    },
    chartData() {
      const metaFields = ['order', 'month', 'label', 'unit'];
      let sortedData = [];
      const datasets = [];

      const isDataUnavailable = (data, key) => data.map(item => item[key]).every(item => includes([0, null, undefined], item))

      if (this.type === 'MARKET_AND_FINANCIAL') {
        sortedData = Array.isArray(this.majorAssumptions) ? [...this.majorAssumptions].sort((a, b) => a.order - b.order) : [];
        let actualKey, budgetKey, outlookKey;
        if (this.activeSubTab === 'exchange') {
          actualKey = 'avg_exchange_rate';
          budgetKey = 'budget_exchange_rate';
          outlookKey = 'avg_outlook_exchange_rate';
        } else if (this.activeSubTab === 'fuel') {
          actualKey = 'avg_price_usd';
          budgetKey = 'budget_price_usd';
          outlookKey = 'avg_outlook_price_usd';
        }
        if (actualKey) {
          const isHidden = isDataUnavailable(sortedData, actualKey)
          datasets.push({
            label: 'Actual',
            backgroundColor: '#FF9640',
            borderColor: '#FF9640',
            data: sortedData.map(item => item[actualKey] === 0 ? null : item[actualKey] ?? null),
            fill: false,
            hidden: isHidden
          });
        }
        if (budgetKey) {
          const isHidden = isDataUnavailable(sortedData, budgetKey)
          datasets.push({
            label: 'Budget',
            backgroundColor: '#6C8AE4',
            borderColor: '#6C8AE4',
            data: sortedData.map(item => item[budgetKey] === 0 ? null : item[budgetKey] ?? null),
            fill: false,
            hidden: isHidden
          });
        }
        if (outlookKey) {
          const isHidden = isDataUnavailable(sortedData, outlookKey)
          datasets.push({
            label: 'Outlook',
            backgroundColor: '#7CB66D',
            borderColor: '#7CB66D',
            data: sortedData.map(item => item[outlookKey] === 0 ? null : item[outlookKey] ?? null),
            fill: false,
            hidden: isHidden
          });
        }
      } else if (this.type === 'VOLUME_AND_SALES') {
        sortedData = Array.isArray(this.majorAssumptions) ? [...this.majorAssumptions].sort((a, b) => a.order - b.order) : [];
        let actualKey, budgetKey, outlookKey;
        if (this.activeSubTab === 'volume_exc_3rd_party') {
          actualKey = 'total_actual_tonnage_exc_third_party';
          budgetKey = 'total_budget_tonnage_exc_third_party';
          outlookKey = 'total_outlook_tonnage_exc_third_party';
        } else if (this.activeSubTab === 'volume') {
          actualKey = 'total_actual_tonnage';
          budgetKey = 'total_budget_tonnage';
          outlookKey = 'total_outlook_tonnage';
        }
        if (actualKey) {
          const isHidden = isDataUnavailable(sortedData, actualKey)
          datasets.push({
            label: 'Actual',
            backgroundColor: '#FF9640',
            borderColor: '#FF9640',
            data: sortedData.map(item => item[actualKey] === 0 ? null : item[actualKey] ?? null),
            fill: false,
            hidden: isHidden
          });
        }
        if (budgetKey) {
          const isHidden = isDataUnavailable(sortedData, budgetKey)
          datasets.push({
            label: 'Budget',
            backgroundColor: '#6C8AE4',
            borderColor: '#6C8AE4',
            data: sortedData.map(item => item[budgetKey] === 0 ? null : item[budgetKey] ?? null),
            fill: false,
            hidden: isHidden
          });
        }
        if (outlookKey) {
          const isHidden = isDataUnavailable(sortedData, outlookKey)
          datasets.push({
            label: 'Outlook',
            backgroundColor: '#7CB66D',
            borderColor: '#7CB66D',
            data: sortedData.map(item => item[outlookKey] === 0 ? null : item[outlookKey] ?? null),
            fill: false,
            hidden: isHidden
          });
        }
      }
      else {
        const subTabData = this.majorAssumptions[this.activeSubTab] || [];
        sortedData = Array.isArray(subTabData) ? [...subTabData].sort((a, b) => a.order - b.order) : [];
        const keys = Array.from(new Set(sortedData.reduce((acc, item) => acc.concat(Object.keys(item || {})), [])));
        const actualKey = keys.find(k => k.includes('actual_'));
        const budgetKey = keys.find(k => k.includes('budget_'));
        const outlookKey = keys.find(k => k.includes('outlook_'));
        if (actualKey) {
          const isHidden = isDataUnavailable(sortedData, actualKey)
          datasets.push({
            label: 'Actual',
            backgroundColor: '#FF9640',
            borderColor: '#FF9640',
            data: sortedData.map(item => item[actualKey] === 0 ? null : item[actualKey] ?? null),
            fill: false,
            hidden: isHidden
          });
        }
        if (budgetKey) {
          const isHidden = isDataUnavailable(sortedData, budgetKey)
          datasets.push({
            label: 'Budget',
            backgroundColor: '#6C8AE4',
            borderColor: '#6C8AE4',
            data: sortedData.map(item => item[budgetKey] === 0 ? null : item[budgetKey] ?? null),
            fill: false,
            hidden: isHidden
          });
        }
        if (outlookKey) {
          const isHidden = isDataUnavailable(sortedData, outlookKey)
          datasets.push({
            label: 'Outlook',
            backgroundColor: '#7CB66D',
            borderColor: '#7CB66D',
            data: sortedData.map(item => item[outlookKey] === 0 ? null : item[outlookKey] ?? null),
            fill: false,
            hidden: isHidden
          });
        }
      }
      return {
        labels: sortedData.map(item => item.label),
        datasets
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        borderRadius: {
          topLeft: 5,
          topRight: 5,
          bottomLeft: 5,
          bottomRight: 5
        },
        layout: {
          padding: {
            left: 25,
            top: 50,
            right: 25
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: false,
              text: this.yAxisLabel
            },
            grid: {
              display: false
            },
            ticks: {
              color: 'black',
              font: {
                family: 'Poppins'
              }
            }
          },
          x: {
            beginAtZero: true,
            offset: true,
            title: {
              display: true,
              text: ''
            },
            grid: {
              display: false
            }
          }
        },
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            onClick: null,
            labels: {
              generateLabels: function(chart) {
                const data = chart.data;
                return data.datasets.map((dataset, i) => ({
                  text: dataset.label,
                  fillStyle: dataset.backgroundColor,
                  hidden: false,
                  lineCap: dataset.borderCapStyle,
                  lineDash: [],
                  lineDashOffset: 0,
                  lineJoin: dataset.borderJoinStyle,
                  lineWidth: dataset.borderWidth,
                  strokeStyle: dataset.borderColor,
                  pointStyle: dataset.pointStyle,
                  datasetIndex: i
                }));
              }
            }
          },
          chartAreaBorder: {
            borderColor: 'black',
            borderWidth: 2
          },
          customTitle: {
            y: {
              display: true,
              text: this.yAxisLabel,
              color: '#000000',
              font: {
                family: 'Poppins',
                size: 14,
                weight: 600
              }
            }
          },
          axisIcons: true,
          tooltip: {
            titleColor: '#000000',
            enabled: true,
            backgroundColor: '#FAFDFF',
            bodyColor: '#000000',
            bodyFont: {
              family: 'Poppins',
              weight: 'normal'
            },
            borderWidth: 1,
            cornerRadius: 6,
            displayColors: false,
            callbacks: {
              label: ctx => {
                const value = ctx.raw;
                const label = ctx.dataset?.label || '';
                const unit = this.yAxisLabel ? ` ${this.yAxisLabel}` : '';
                return value !== null && value !== undefined
                  ? `${label.charAt(0).toUpperCase() + label.slice(1)}: ${this.formatSmartNumber(value)}${unit}`
                  : '';
              }
            }
          }
        }
      };
    },
    chartPlugins() {
      return [
        this.axisIconPlugin(),
        this.chartAreaBorderPlugin(),
        this.customTitlePlugin(),
        this.valueLabelPlugin()
      ]
    }
  },
  methods: {
    async fetchMajorAssumptions() {
      const filters = {
        type: this.type,
        ...this.filters
      }
      try {
        await this.$store.dispatch('CostAnalysis/fetchMajorAssumptions', filters);
      } catch (err) {
        throw err;
      }
    },
    setActiveTab(tab) {
      this.activeTab = tab;
      const firstEnabled = (this.subTabsMap[tab] || []).find(st => !st.disabled);
      this.activeSubTab = firstEnabled ? firstEnabled.key : '';
      this.type = this.tabTypeMap[tab];
    },
    setActiveSubTab(subTabKey) {
      if (!(this.subTabs || []).find(st => st.key === subTabKey)?.disabled) {
        this.activeSubTab = subTabKey;
      }
    },
    axisIconPlugin() {
      return {
        id: 'axisIcons',
        beforeDraw(chart, args, options) {
          const { ctx, chartArea: { top, left, right, bottom }, scales } = chart;

          const topIcon = {
            text: '\u2BC5',
            x: left,
            y: top - 15,
            color: '#000',
            font: 'bold 20px FontAwesome'
          };

          const rightIcon = {
            text: '\u2BC8',
            x: right + 15,
            y: bottom + 2,
            color: '#000',
            font: 'bold 20px Poppins'
          }

          ctx.save()
          ctx.font = topIcon.font
          ctx.fillStyle = topIcon.color
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(topIcon.text, topIcon.x, topIcon.y)
          ctx.restore();

          ctx.save()
          ctx.font = rightIcon.font
          ctx.fillStyle = rightIcon.color
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(rightIcon.text, rightIcon.x, rightIcon.y)
          ctx.restore()
        }
      }
    },
    chartAreaBorderPlugin() {
      return {
        id: 'chartAreaBorder',
        beforeDraw(chart, args, options) {
          const {ctx, chartArea: {left, top, width, height}} = chart
          const extension = 15
          const padding = 1

          ctx.save();
          ctx.strokeStyle = options.borderColor || 'black'
          ctx.lineWidth = options.borderWidth || 2

          ctx.beginPath();
          ctx.moveTo(left, top - extension)
          ctx.lineTo(left, top + height + padding)
          ctx.stroke()

          ctx.beginPath()
          ctx.moveTo(left, top + height + padding)
          ctx.lineTo(left + width + extension, top + height + padding)
          ctx.stroke();

          ctx.restore()
        }
      }
    },
    customTitlePlugin() {
      return {
        id: 'customTitle',
        afterDraw(chart, args, options) {
          const { ctx, chartArea: { top, left, width } } = chart

          if (options?.y?.display) {
            ctx.save()
            ctx.fillStyle = options.y.color || 'black'

            const font = options.y.font || {}
            const fontSize = font.size || 10;
            const fontFamily = font.family || 'sans-serif'
            const fontWeight = font.weight || 'normal'
            ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`

            ctx.textAlign = 'center'
            ctx.textBaseline = 'top'

            const xPos = left / 2
            const yPos = top - 25

            ctx.fillText(options.y.text, xPos, yPos)
            ctx.restore()
          }
        }
      }
    },
    formatSmartNumber(val) {
      if (val == null) return '';
      const num = Number(val);
      if (Number.isInteger(num)) return handleValidateAndFormatNumber(num);
      if (num % 1 === 0) return handleValidateAndFormatNumber(num);
      return handleValidateAndFormatNumber(num);
    },
    valueLabelPlugin() {
      return {
        id: 'valueLabel',
        afterDatasetsDraw: chart => {
          const ctx = chart.ctx;
          ctx.save();
          chart.data.datasets.forEach((dataset, datasetIndex) => {
            const meta = chart.getDatasetMeta(datasetIndex);
            meta.data.forEach((bar, i) => {
              const value = dataset.data[i];
              if (value == null) return;
              const text = this.formatSmartNumber(value);
              ctx.font = 'normal 12px Poppins';
              ctx.fillStyle = '#222';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'bottom';
              let yDynamic;
              if (text.length > 5) {
                yDynamic = bar.y - 30;
              } else {
                yDynamic = bar.y - 25;
              }
              const x = bar.x + 8;
              const y = yDynamic;
              ctx.save();
              ctx.translate(x, y);
              ctx.rotate(-Math.PI / 2);
              ctx.fillText(text, 0, 0);
              ctx.restore();
            });
          });
          ctx.restore();
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.tabs {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  .tab {
    padding: 20px 40px;
    border: 1px solid #ccc;
    border-color: #ccc;
    border-radius: 10px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    width: 100%;
    background: #FFFFFF;
    &.active {
      background-color: #6546C3;
      color: #fff;
    }
  }
}
.sub-tabs {
  display: flex;
  gap: 8px;
  .sub-tab {
    background: #FFFFFF;
    border: none;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    padding: 10px 16px;
    color: #A0A0A0;
    font-weight: 500;
    cursor: pointer;
    font-size: 14px;
    &.active {
      background: #FF7E57;
      color: #fff;
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
.chart-container {
  background: #fff;
  border-radius: 8px;
  border-top-left-radius: 0px;
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}
.chart-placeholder {
  color: #bbb;
  font-size: 18px;
}
</style> 